import { createSelector } from 'reselect';

import { StateSchema } from 'app/providers/StoreProvider';

export const getIsSourcingInProgress = (state: StateSchema) =>
    state.editOrderItemSourcing?.isSourcingInProgress || false;

export const getIsSourcingOverridden = (state: StateSchema) =>
    state.editOrderItemSourcing?.currentState?.isSourcingOverridden || false;

export const getIsLoading = (state: StateSchema) =>
    state.editOrderItemSourcing?.isLoading || false;

export const getHasUnsavedSourcingChanges = (state: StateSchema) =>
    state.editOrderItemSourcing?.isDirty || false;

export const getTotalQuantity = createSelector(
    [(state: StateSchema) => state.editOrderItemSourcing?.currentState],
    (currentState) => currentState?.totalQuantity || 0,
);

export const getSupplierQuantities = createSelector(
    [(state: StateSchema) => state.editOrderItemSourcing?.currentState],
    (currentState) => currentState?.selectedProposals || [],
);

export const getCanSave = createSelector(
    [
        (state: StateSchema) => state.editOrderItemSourcing?.isDirty || false,
        (state: StateSchema) =>
            state.editOrderItemSourcing?.validationErrors || [],
        (state: StateSchema) => state.editOrderItemSourcing?.isLoading || false,
        (state: StateSchema) =>
            state.editOrderItemSourcing?.isSourcingInProgress || false,
    ],
    (isDirty, validationErrors, isLoading, isSourcingInProgress) =>
        isDirty &&
        validationErrors.length === 0 &&
        !isLoading &&
        !isSourcingInProgress,
);

export const getCanEditSuppliers = createSelector(
    [getIsSourcingInProgress],
    (isSourcingInProgress) => !isSourcingInProgress,
);

export const getIsInitialized = createSelector(
    [
        (state: StateSchema) => state.editOrderItemSourcing?.currentState,
        (state: StateSchema) => state.editOrderItemSourcing?.itemId,
        (state: StateSchema) => state.editOrderItemSourcing?.orderId,
    ],
    (currentState, itemId, orderId) =>
        Boolean(currentState && itemId && orderId),
);

export const getTotalSelectedPrice = createSelector(
    [(state: StateSchema) => state.editOrderItemSourcing?.currentState],
    (currentState): number => {
        if (!currentState?.selectedProposals) return 0;

        return currentState.selectedProposals.reduce(
            (acc, selectedProposal) => {
                return (
                    acc +
                    (selectedProposal.price || 0) *
                        (selectedProposal.selectedQuantity || 0)
                );
            },
            0,
        );
    },
);

export const getShowTotalQuantityError = (state: StateSchema) =>
    state.editOrderItemSourcing?.showTotalQuantityError || false;

export const getZeroTotalQuantityError = createSelector(
    [
        (state: StateSchema) =>
            state.editOrderItemSourcing?.validationErrors || [],
    ],
    (validationErrors) =>
        validationErrors.some(
            (error) =>
                error.field === 'totalQuantity' &&
                error.message === 'Total quantity must be greater than 0',
        ),
);

export const getQuantityExceedsStockError = createSelector(
    [
        (state: StateSchema) =>
            state.editOrderItemSourcing?.validationErrors || [],
    ],
    (validationErrors) => {
        return validationErrors.some(
            (error) =>
                error.field === 'totalQuantity' &&
                error.message.includes(
                    'must be less than or equal to available quantity',
                ),
        );
    },
);

export const getSupplierActivationStatusError = createSelector(
    [
        (state: StateSchema) =>
            state.editOrderItemSourcing?.validationErrors || [],
    ],
    (validationErrors) => {
        return validationErrors.some(
            (error) =>
                error.field === 'supplierQuantities' &&
                error.message.includes(
                    'has an invalid or pending activation status',
                ),
        );
    },
);
